/**
 * Auto-Alert API Service
 * Frontend service for intelligent auto-alerting
 */

import apiClient from './api.service';

// Types
export interface AutoAlertConfig {
  id?: string;
  tenantId: string;
  applicationId?: string;
  enabled: boolean;
  slowQueryThresholdMs: number;
  errorRateThresholdPercent: number;
  responseTimeThresholdMs: number;
  checkIntervalMinutes: number;
  aiAnalysisEnabled: boolean;
  notificationChannels: {
    email?: boolean;
    panel?: boolean;
    crm?: {
      enabled?: boolean;
      whatsapp?: boolean;
      telegram?: boolean;
      call?: boolean;
      discord?: boolean;
    };
  };
  recipients: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface DetectedIssue {
  type: 'slow_query' | 'error_spike' | 'performance_degradation' | 'high_error_rate';
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  metric: string;
  currentValue: number;
  threshold: number;
  description: string;
  affectedService?: string;
  sampleData?: any;
  aiAnalysis?: {
    summary: string;
    rootCause: string;
    impact: string;
    recommendations: string[];
    confidenceScore: number;
  };
}

export interface AutoAlertResult {
  tenantId: string;
  applicationId?: string;
  timestamp: string;
  issuesDetected: number;
  issues: DetectedIssue[];
  alertsCreated: number;
  notificationsSent: number;
  duration: number;
}

export interface AutoAlertStatus {
  aiEnabled: boolean;
  configuredAlerts: number;
  activeAlerts: number;
  configurations: Array<{
    applicationId?: string;
    enabled: boolean;
    slowQueryThresholdMs: number;
    errorRateThresholdPercent: number;
    checkIntervalMinutes: number;
    aiAnalysisEnabled: boolean;
    updatedAt?: string;
  }>;
}

export interface QueryAnalysisResult {
  originalQuery: string;
  analysis: string;
  optimizedQuery?: string;
  indexSuggestions: string[];
  estimatedImprovement: string;
  recommendations: string[];
}

export interface ThresholdRecommendations {
  slowQueryThresholdMs: number;
  errorRateThresholdPercent: number;
  responseTimeThresholdMs: number;
  reasoning: {
    slowQuery: string;
    errorRate: string;
    responseTime: string;
  };
}

// API Functions
export const autoAlertApi = {
  /**
   * Get auto-alert status and AI availability
   */
  async getStatus(): Promise<AutoAlertStatus> {
    const response = await apiClient.get('/v1/auto-alerts/status');
    return response.data;
  },

  /**
   * Get configuration for a specific application or tenant-level
   */
  async getConfig(applicationId?: string): Promise<AutoAlertConfig> {
    const url = applicationId
      ? `/v1/auto-alerts/config/${applicationId}`
      : '/v1/auto-alerts/config';
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Update auto-alert configuration
   */
  async updateConfig(config: Partial<AutoAlertConfig>): Promise<AutoAlertConfig> {
    const response = await apiClient.put('/v1/auto-alerts/config', config);
    return response.data;
  },

  /**
   * Delete auto-alert configuration
   */
  async deleteConfig(applicationId?: string): Promise<{ deleted: boolean }> {
    const url = applicationId
      ? `/v1/auto-alerts/config/${applicationId}`
      : '/v1/auto-alerts/config';
    const response = await apiClient.delete(url);
    return response.data;
  },

  /**
   * Trigger manual detection
   */
  async runDetection(applicationId?: string): Promise<AutoAlertResult> {
    const response = await apiClient.post('/v1/auto-alerts/run', { applicationId });
    return response.data;
  },

  /**
   * Analyze a specific query with AI
   */
  async analyzeQuery(
    query: string,
    executionTime: number,
    databaseType: string,
    tableSchema?: Record<string, any>
  ): Promise<QueryAnalysisResult> {
    const response = await apiClient.post('/v1/auto-alerts/analyze-query', {
      query,
      executionTime,
      databaseType,
      tableSchema,
    });
    return response.data;
  },

  /**
   * Get recent auto-alert detections
   */
  async getHistory(
    applicationId?: string,
    limit?: number
  ): Promise<{ detections: AutoAlertResult[]; total: number }> {
    const params: Record<string, any> = {};
    if (applicationId) params.applicationId = applicationId;
    if (limit) params.limit = limit;

    const response = await apiClient.get('/v1/auto-alerts/history', { params });
    return response.data;
  },

  /**
   * Get threshold recommendations based on historical data
   */
  async getRecommendations(applicationId: string): Promise<ThresholdRecommendations> {
    const response = await apiClient.get(`/v1/auto-alerts/recommendations/${applicationId}`);
    return response.data;
  },
};

export default autoAlertApi;
